import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { extractEdgeFunctionError } from "@/lib/edgeFunctionError";
import { useAuth } from "@/hooks/useAuth";

export interface AdminMfaStatus {
  enrolled: boolean;
  session_verified: boolean;
  step_up_fresh: boolean;
  step_up_expires_at: string | null;
  recovery_codes_remaining: number;
  recovery_codes_used: number;
  is_super_admin: boolean;
  session_ttl_minutes: number;
}

async function callMfa<T = any>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-mfa", { body });
  if (error || (data as any)?.error) {
    throw new Error(await extractEdgeFunctionError(error, data, "Two-factor request failed"));
  }
  return data as T;
}

interface AdminMfaContextValue {
  status: AdminMfaStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  startEnrolment: () => Promise<{ secret: string; uri: string }>;
  confirmEnrolment: (code: string) => Promise<string[]>;
  verifySession: (code: string) => Promise<void>;
  verifyRecoveryCode: (code: string) => Promise<void>;
  stepUp: (code: string, intent: string) => Promise<void>;
  regenerateRecoveryCodes: (code: string) => Promise<string[]>;
}

const AdminMfaContext = createContext<AdminMfaContextValue | undefined>(undefined);

export function AdminMfaProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<AdminMfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus(null);
      setLoading(false);
      return;
    }
    try {
      const res = await callMfa<AdminMfaStatus>({ action: "status" });
      if (mounted.current) {
        setStatus(res);
        setError(null);
      }
    } catch (err) {
      if (mounted.current) setError((err as Error).message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-check when the tab regains focus so an expired session prompts again.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const value = useMemo<AdminMfaContextValue>(() => ({
    status,
    loading,
    error,
    refresh,
    startEnrolment: async () => {
      return await callMfa<{ secret: string; uri: string }>({ action: "enrol-start" });
    },
    confirmEnrolment: async (code: string) => {
      const res = await callMfa<{ recovery_codes: string[] }>({ action: "enrol-confirm", code });
      await refresh();
      return res.recovery_codes || [];
    },
    verifySession: async (code: string) => {
      await callMfa({ action: "verify", code });
      await refresh();
    },
    verifyRecoveryCode: async (code: string) => {
      await callMfa({ action: "verify-recovery", code });
      await refresh();
    },
    stepUp: async (code: string, intent: string) => {
      await callMfa({ action: "step-up", code, intent });
      await refresh();
    },
    regenerateRecoveryCodes: async (code: string) => {
      const res = await callMfa<{ recovery_codes: string[] }>({ action: "regenerate-recovery-codes", code });
      await refresh();
      return res.recovery_codes || [];
    },
  }), [status, loading, error, refresh]);

  return <AdminMfaContext.Provider value={value}>{children}</AdminMfaContext.Provider>;
}

export function useAdminMfa() {
  const ctx = useContext(AdminMfaContext);
  if (!ctx) throw new Error("useAdminMfa must be used inside AdminMfaProvider");
  return ctx;
}

/** Direct call helper for screens outside the provider (e.g. super-admin tools). */
export const adminMfaCall = callMfa;
